import React, { useCallback, useEffect, useState } from "react";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { useSelector } from "react-redux";
import { PAGES } from "../../../enums/PagesEnum";
import { toast } from "react-toastify";
import getAxiosError from "../../../app/api/axiosError";
import { Box, Grid, MenuItem, TextField } from "@mui/material";
import SubmitModalWithEditAndDelete from "../../../components/common/modalPopup/SubmitModalWithEditAndDelete";
import { ZipCoverageResponse } from "../../../app/models/responseModels/zipCoverageResponse";
import { UpdateZipCoverageRequest } from "../../../app/models/requestModels/zipCoverageRequest";
import {
  deleteZipCoverage,
  updateZipCoverage,
} from "../../../app/features/zipCoverage/zipCoverageSlice";
import Loader from "../../../components/common/loader/Loader";
import { getparcelCenterList } from "../../../app/features/parcelCenter/parcelCenterSlice";

interface EditZipCoverageModalProps {
  closeEditZipCoverageModal: CallableFunction;
  onCompleted: CallableFunction;
  zipCoverageRowData: ZipCoverageResponse;
}

function UpdateZipCoverageModel(
  id: string,
  parcelCenterId: string,
  zipCode: string
): UpdateZipCoverageRequest {
  return {
    id: id,
    parcelCenterId: parcelCenterId,
    zipCode: zipCode,
  };
}

const EditZipCoverageModal = ({
  closeEditZipCoverageModal,
  onCompleted,
  zipCoverageRowData,
}: EditZipCoverageModalProps) => {
  const dispatch = useAppDispatch();

  const { isLoading: isParcelCenterListLoading, parcelCenterList } =
    useSelector((state: RootState) => state.parcelCenter);

  const { zipCoverageList } = useSelector(
    (state: RootState) => state.zipCoverage
  );

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.ZIP_COVERAGE
    )
  );

  const IsUpdatePermisssion = pagePermission
    ? pagePermission.isUpdatePermission
    : false;
  const IsDeletePermisssion = pagePermission
    ? pagePermission.isDeletePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  // ***** States *****
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [parcelCenterId, setParcelCenterId] = useState(
    zipCoverageRowData.parcelCenterId
  );
  const [zipCode, setZipCode] = useState(zipCoverageRowData.zipCode);
  const oldParcelCenterId = zipCoverageRowData.parcelCenterId;

  // ***** Callbacks *****
  const getParcelCenterData = useCallback(async () => {
    try {
      const response = await dispatch(getparcelCenterList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "parcelCenterList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "parcelCenterListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getParcelCenterData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getParcelCenterData]);
  // ***** Handlers *****

  const handleParcelCenterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let selectedOption = event.target.value as string;
    setParcelCenterId(selectedOption);
  };

  const handleUpdate = async () => {
    try {
      if (parcelCenterId.length === 0) {
        toast.error("Parcel Center is required");
        return false;
      }
      if (zipCode.length === 0) {
        toast.error("Zip Code is required");
        return false;
      }
      let filteredZipCode = zipCoverageList.filter(
        (x) => x.zipCode === zipCode && x.parcelCenterId === parcelCenterId
      );
      if (filteredZipCode.length === 1) {
        toast.error("Zip Code is already mapped to selected parcel center");
        return false;
      }
      filteredZipCode = zipCoverageList.filter((x) => x.zipCode === zipCode);
      if (
        filteredZipCode.length === 1 &&
        oldParcelCenterId === parcelCenterId
      ) {
        toast.error("Zip Code is already mapped to another parcel center");
        return false;
      }

      const model = UpdateZipCoverageModel(
        zipCoverageRowData.id,
        parcelCenterId,
        zipCode
      );
      const response = await dispatch(updateZipCoverage(model)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "updateZipCoverageSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "updateZipCoverageError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "updateZipCoverageException",
      });
    }
    return true;
  };
  const handleDelete = async () => {
    try {
      const model = UpdateZipCoverageModel(
        zipCoverageRowData.id,
        parcelCenterId,
        zipCode
      );
      const response = await dispatch(deleteZipCoverage(model)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "deleteZipCoverageSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "deleteZipCoverageError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "deleteZipCoverageException",
      });
    }
    return true;
  };

  return (
    <>
      <SubmitModalWithEditAndDelete
        title="Update Zip Coverage"
        updateAsyncOperation={handleUpdate}
        closeSubmitModalWithEditAndDelete={closeEditZipCoverageModal}
        size="lg"
        isLoading={false}
        submitButtonName="Save"
        closeModalAfterSubmit={true}
        confirmMessage="Are you sure you want to delete?"
        isUpdatePermission={IsUpdatePermisssion && IsAdmin}
        isDeletePermission={IsDeletePermisssion}
        setShowSubmitButton={setShowSubmitButton}
        showSubmitButton={showSubmitButton}
        showDeleteButton={IsDeletePermisssion && IsAdmin}
        deleteAsyncOperation={handleDelete}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              {isParcelCenterListLoading ? (
                <Loader isLoading={isParcelCenterListLoading} />
              ) : (
                <TextField
                  select
                  margin="normal"
                  id="parcelCenter"
                  label="Parcel Center"
                  name="parcelCenter"
                  size="small"
                  fullWidth
                  required
                  disabled={!showSubmitButton}
                  value={parcelCenterId}
                  onChange={handleParcelCenterChange}
                >
                  {parcelCenterList.map((Option, index) => (
                    <MenuItem
                      value={Option.parcelCenterId}
                      key={Option.parcelCenterId}
                    >
                      {Option.parcelCenterName}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                margin="normal"
                id="zipcode"
                label="Zipcode"
                name="zipcode"
                size="small"
                fullWidth
                required
                disabled={!showSubmitButton}
                value={zipCode}
                onChange={(event) => setZipCode(event.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
      </SubmitModalWithEditAndDelete>
    </>
  );
};

export default EditZipCoverageModal;
